import React, { useState } from "react";
import { Edit, Mail } from "lucide-react";

const TrainingsTable = ({ trainings, onEditTraining, onStatusChange, onMailReminder }) => {
  const [sortField, setSortField] = useState('start_date');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedTrainings = [...trainings].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortField) {
      case 'start_date':
      case 'end_date':
        aValue = new Date(a[sortField]);
        bValue = new Date(b[sortField]);
        break;
      case 'name':
        aValue = (a.name || 'Unnamed Training').toLowerCase();
        bValue = (b.name || 'Unnamed Training').toLowerCase();
        break;
      case 'qualification_name':
      case 'training_center_name':
      case 'status':
        aValue = (a[sortField] || '').toLowerCase();
        bValue = (b[sortField] || '').toLowerCase();
        break;
      case 'participants_count':
        aValue = a.participants_count || 0;
        bValue = b.participants_count || 0;
        break;
      case 'created_by':
        aValue = (a.created_by || '').toLowerCase();
        bValue = (b.created_by || '').toLowerCase();
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const SortableHeader = ({ field, children }) => (
    <th
      className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider border-b-2 border-brand-500 cursor-pointer hover:bg-steel-100 select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortField === field && (
          <span className="text-brand-500">
            {sortDirection === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </th>
  );

  return (
    <div className="overflow-x-auto rounded-xl">
      <table className="min-w-full bg-white border border-steel-200 rounded-xl">
        <thead className="bg-steel-100">
          <tr>
            <SortableHeader field="start_date">Start Date</SortableHeader>
            <SortableHeader field="end_date">End Date</SortableHeader>
            <SortableHeader field="name">Training Name</SortableHeader>
            <SortableHeader field="qualification_name">Qualification</SortableHeader>
            <SortableHeader field="training_center_name">Training Center</SortableHeader>
            <SortableHeader field="participants_count">Participants</SortableHeader>
            <SortableHeader field="created_by">Created By</SortableHeader>
            <SortableHeader field="status">Status</SortableHeader>
            <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider border-b-2 border-brand-500">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-steel-200">
          {sortedTrainings.map((training) => (
            <tr key={training.id} className="hover:bg-brand-50 transition-colors">
              <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-900">
                {new Date(training.start_date).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
              </td>
              <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-900">
                {new Date(training.end_date).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
              </td>
              <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-900">
                {training.name || 'Unnamed Training'}
              </td>
              <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-900">
                {training.qualification_name}
              </td>
              <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-900">
                {training.training_center_name}
              </td>
              <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-900">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  training.availability && training.participants_count === training.availability
                    ? 'bg-red-100 text-red-800'
                    : 'bg-green-100 text-green-800'
                }`}>
                  {training.participants_count}{training.availability ? ` / ${training.availability}` : ''}
                </span>
              </td>
              <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-900">
                {training.created_by || '-'}
              </td>
              <td className="px-4 py-2 whitespace-nowrap">
                <select
                  value={training.status || 'planned'}
                  onChange={(e) => onStatusChange(training.id, e.target.value, training.status, training)}
                  className={`inline-block px-3 py-1 text-xs font-semibold rounded-full border-0 focus:ring-2 focus:ring-brand-500 appearance-none min-w-[100px] ${
                    training.status === 'completed' ? 'bg-brand-100 text-brand-700' :
                    training.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                    training.status === 'approved' ? 'bg-green-100 text-green-800' :
                    training.status === 'planned' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-steel-100 text-slate-800'
                  }`}
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.5rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1rem 1rem',
                    paddingRight: '2rem'
                  }}
                >
                  <option value="planned">planned</option>
                  <option value="approved">approved</option>
                  <option value="completed">completed</option>
                  <option value="cancelled">cancelled</option>
                </select>
              </td>
              <td className="px-4 py-2 whitespace-nowrap text-right text-sm font-medium">
                <button
                  onClick={() => onEditTraining(training)}
                  className="text-brand-500 hover:text-brand-600 mr-3"
                  title="Edit Training"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onMailReminder(training)}
                  className="text-green-600 hover:text-green-800"
                  title="Send Reminder Email"
                >
                  <Mail className="h-4 w-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TrainingsTable;